import { lotteries, rules_lotteries, type_currency, countries } from '~/utils/constants/home.js'
import { rules_lotteries as rules_lotteries_multiple_bets } from '~/utils/constants/multipleBets.js'

/**
 * Returns the lotteries available for a given country object.
 * Falls back to the first lottery in the catalogue when the country has no specific list.
 * @param {object} country - Country object with a `lotteries` id-array.
 */
export const fetchLotteries = (country) => {
	const listLotteries = country?.lotteries || []
	return listLotteries.length > 0 ? lotteries.filter((lottery) => listLotteries.includes(lottery.id)) : [lotteries[0]]
}

/**
 * Returns the lotteries that are NOT available for the given country (the complement set).
 * @param {object} country - Country object with a `lotteries` id-array.
 */
export const fetchOthersLotteries = (country) => {
	const listLotteries = country?.lotteries || []

	return listLotteries.length > 0
		? lotteries.filter((lottery) => !listLotteries.includes(lottery.id))
		: lotteries.filter((lottery) => lottery.id !== lotteries[0].id)
}

/**
 * Looks up a lottery by its numeric ID. Returns `null` when not found.
 * @param {number|null} id
 */
export const fetchLotterySelect = (id) => {
	if (id === null) return null
	const lottery = lotteries.find((item) => item.id === id)
	return lottery || null
}

/**
 * Returns the rules object for a lottery, switching to the multiple-bets ruleset when
 * `isMutipleBets` is `true` and the lottery has a dedicated multiple-bets entry.
 * @param {object|null} lotterySelect - Lottery object with a `slug` property.
 * @param {boolean} [isMutipleBets=false]
 */
export const fetchRulesLotterySelect = (lotterySelect, isMutipleBets = false) => {
	if (lotterySelect === null) return null
	const selectRules =
		isMutipleBets === true && rules_lotteries_multiple_bets[lotterySelect.slug]
			? rules_lotteries_multiple_bets
			: rules_lotteries
	const rules = selectRules[lotterySelect.slug]
	return rules ? rules : null
}

/**
 * Same as `fetchRulesLotterySelect` but looks up by numeric ID instead of slug.
 * @param {number|null} lotterySelectId
 * @param {boolean} [isMutipleBets=false]
 */
export const fetchRulesLotteryById = (lotterySelectId, isMutipleBets = false) => {
	if (lotterySelectId === null) return null

	const rulesById = Object.fromEntries(Object.values(rules_lotteries).map((rule) => [rule.id, rule]))

	const rules_multiple_by_id = Object.fromEntries(
		Object.values(rules_lotteries_multiple_bets).map((rule) => [rule.id, rule]),
	)

	const selectRules = isMutipleBets === true ? rules_multiple_by_id : rulesById

	return selectRules[lotterySelectId] || null
}

/**
 * Returns the icon identifier for a currency slug, or `undefined` if not found.
 * @param {string} icon - Currency slug (e.g. `'EUR'`).
 */
export const fetchIcon = (icon) => {
	const item = type_currency.find((currency) => currency.slug === icon)
	return item?.icon
}

/**
 * Returns the display label of the country that offers a given lottery ID.
 * @param {number} idLottery
 * @returns {string|undefined}
 */
export const findCountryByLottery = (idLottery) => {
	const ID_EUROMILLIONS = 'ID_EUROMILLIONS'
	const lottery = countries.find((country) => {
		const lotteries = country.lotteries || [ID_EUROMILLIONS]
		return lotteries.includes(idLottery)
	})
	return lottery?.label
}

/**
 * Returns the country object matching `idCountry`, or `undefined` if not found.
 * @param {number} idCountry
 */
export const findCountryById = (idCountry) => {
	const country = countries.find((country) => country.id === idCountry)
	return country
}
